import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import CorporateFooter from '@/components/layout/CorporateFooter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useAppStore } from '@/store';
import {
  ArrowUpRight,
  Cpu,
  Download,
  ExternalLink,
  Globe,
  Layers3,
  Search,
  Server,
  Shield,
  Sparkles,
  Sun,
} from 'lucide-react';

const MODEL_METADATA_URL = 'https://models2.mapleos.ai/metadata.json';
const PAGE_SIZE = 24;

type QuantizationLegend = Record<string, string>;

interface BaseModelInfo {
  id?: string;
  url?: string;
  author?: string;
  author_url?: string;
  downloads?: number | null;
  likes?: number | null;
  last_modified?: string | null;
  license?: string | null;
  languages?: string[];
  pipeline_tag?: string | null;
}

interface HostedModel {
  model_id: string;
  name: string;
  huggingface_url?: string;
  served_url?: string;
  hf_compat_url?: string;
  author?: string;
  author_url?: string;
  base_model?: BaseModelInfo;
  family?: string;
  description?: string;
  use_cases?: string[];
  quantization?: string;
  quantization_description?: string;
  size_bytes?: number;
  size_gb?: number;
  downloads?: number | null;
  likes?: number | null;
  last_modified?: string | null;
  tags?: string[];
  in_webllm_official?: boolean;
  selection_rationale?: string;
}

interface ModelCatalogMetadata {
  schema: string;
  generated_at: string;
  host: string;
  host_index_url: string;
  model_index_path: string;
  selection_strategy: {
    total_models?: number;
    total_size_gb?: number;
    size_cap_gb?: number;
    size_cap_rationale?: string;
  };
  quantization_legend: QuantizationLegend;
  family_distribution: Record<string, number>;
  models: HostedModel[];
}

const formatCompactNumber = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const sizeBandFor = (sizeGb?: number) => {
  if (sizeGb === undefined || sizeGb === null) return 'all';
  if (sizeGb < 1) return 'tiny';
  if (sizeGb < 2.5) return 'edge';
  return 'workhorse';
};

const matchesSearch = (model: HostedModel, query: string) => {
  const haystack = [
    model.name,
    model.family,
    model.author,
    model.description,
    model.base_model?.id,
    model.base_model?.author,
    ...(model.use_cases || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
};

const Models = () => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { darkMode, toggleDarkMode } = useAppStore();
  const [catalog, setCatalog] = useState<ModelCatalogMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [useCase, setUseCase] = useState('all');
  const [sizeBand, setSizeBand] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(MODEL_METADATA_URL);
        if (!response.ok) {
          throw new Error(`Failed to load model catalog (${response.status})`);
        }
        const data: ModelCatalogMetadata = await response.json();
        if (!cancelled) {
          setCatalog(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load the model catalog right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, useCase, sizeBand]);

  const useCaseOptions = useMemo(() => {
    const cases = new Set<string>();
    catalog?.models.forEach((model) => {
      model.use_cases?.forEach((item) => cases.add(item));
    });
    return Array.from(cases).sort((a, b) => a.localeCompare(b));
  }, [catalog]);

  const featuredModels = useMemo(() => {
    if (!catalog) return [];
    return [...catalog.models]
      .sort((a, b) => (b.downloads || 0) - (a.downloads || 0))
      .slice(0, 6);
  }, [catalog]);

  const filteredModels = useMemo(() => {
    if (!catalog) return [];

    return [...catalog.models]
      .filter((model) => matchesSearch(model, search))
      .filter((model) => (useCase === 'all' ? true : model.use_cases?.includes(useCase)))
      .filter((model) => (sizeBand === 'all' ? true : sizeBandFor(model.size_gb) === sizeBand))
      .sort((a, b) => {
        const officialDiff = Number(Boolean(b.in_webllm_official)) - Number(Boolean(a.in_webllm_official));
        if (officialDiff !== 0) return officialDiff;
        return (b.downloads || 0) - (a.downloads || 0);
      });
  }, [catalog, search, useCase, sizeBand]);

  const visibleModels = filteredModels.slice(0, visibleCount);
  const dashboardLink = '/dashboard';
  const profileName = profile?.name || user?.email?.split('@')[0] || 'User';

  return (
    <MainLayout>
      <div className="min-h-screen w-full bg-background text-foreground">
        <nav className="fixed left-0 top-0 bottom-0 w-[200px] bg-background p-8 z-30 flex-col items-start justify-start space-y-6 pt-24 hidden lg:flex">
          {user && <Link to={dashboardLink} className="text-lg font-medium hover:text-primary transition-colors">Dashboard</Link>}
          <Link to="/privacy" className="text-lg font-medium hover:text-primary transition-colors">Privacy</Link>
          <Link to="/research" className="text-lg font-medium hover:text-primary transition-colors">Research</Link>
          <Link to="/models" className="text-lg font-medium text-primary transition-colors">Models</Link>
          <Link to="/enterprise" className="text-lg font-medium hover:text-primary transition-colors">Enterprise</Link>
        </nav>

        <header className="fixed inset-x-0 top-0 z-20 flex justify-end p-4 bg-background border-b border-border">
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Button asChild variant="outline">
                  <Link to={dashboardLink}>Dashboard</Link>
                </Button>
                <Button
                  type="button"
                  variant={darkMode ? 'secondary' : 'ghost'}
                  onClick={() => toggleDarkMode('light')}
                  className="gap-2"
                  aria-label="Switch to light mode"
                >
                  <Sun className="h-4 w-4" />
                  <span className="hidden sm:inline">Light mode</span>
                </Button>
                <Button asChild variant="ghost" className="h-10 px-2">
                  <Link to="/profile" className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar} alt={profileName} />
                      <AvatarFallback>{profileName.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline max-w-[120px] truncate">{profileName}</span>
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost">
                  <Link to="/auth">Login</Link>
                </Button>
                <Button asChild>
                  <Link to="/auth">Register</Link>
                </Button>
              </>
            )}
          </div>
        </header>

        <main className="flex flex-col flex-1 px-4 pt-24 pb-12 lg:pl-[200px]">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 md:px-6 lg:py-2">
            <section className="overflow-hidden rounded-3xl border bg-gradient-to-br from-card via-card to-canada-red/5">
              <div className="grid gap-8 px-6 py-8 lg:grid-cols-[1.35fr_0.95fr] lg:px-10 lg:py-10">
                <div className="space-y-5">
                  <Badge className="w-fit bg-canada-red text-white hover:bg-canada-red/90">Canada-hosted model library</Badge>
                  <div className="space-y-3">
                    <h1 className="max-w-4xl text-4xl font-semibold tracking-tight md:text-5xl">
                      A mini Hugging Face for Canada — built for real use, not just browsing.
                    </h1>
                    <p className="max-w-3xl text-base leading-7 text-muted-foreground md:text-lg">
                      This is the CanXP AI model shelf: a growing catalog of models we host in Canada for teams that want fast,
                      practical access to on-device AI. Not every model here was trained in Canada, but every one of them is part of a
                      Canada-hosted delivery layer built to make these systems actually usable.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Badge variant="outline">Hosted in Canada</Badge>
                    <Badge variant="outline">On-device ready</Badge>
                    <Badge variant="outline">WebLLM supported</Badge>
                    <Badge variant="outline">ONNX supported</Badge>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button asChild className="bg-canada-red text-white hover:bg-canada-red/90">
                      <a href="https://models2.mapleos.ai/" target="_blank" rel="noreferrer">
                        Open hosted index
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                    <Button asChild variant="outline">
                      <a href={MODEL_METADATA_URL} target="_blank" rel="noreferrer">
                        View metadata feed
                        <ArrowUpRight className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                  <Card className="border-border/70 bg-background/70 backdrop-blur">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-canada-red">
                        <Server className="h-5 w-5" />
                        <CardTitle className="text-lg">Hosted access</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-6 text-muted-foreground">
                        Browse a live hosted catalog instead of chasing links across the web. This page turns your model shelf into something
                        teams can actually explore.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-border/70 bg-background/70 backdrop-blur">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-canada-red">
                        <Cpu className="h-5 w-5" />
                        <CardTitle className="text-lg">On-device focus</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-6 text-muted-foreground">
                        We support both WebLLM and ONNX model workflows, with the live index below currently reflecting the WebLLM-ready hosted catalog.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-border/70 bg-background/70 backdrop-blur">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-canada-red">
                        <Shield className="h-5 w-5" />
                        <CardTitle className="text-lg">Canada-first positioning</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-6 text-muted-foreground">
                        The point is simple: give people access to strong models through Canadian infrastructure, without pretending every useful model has to begin here.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </section>

            <section className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total hosted models</CardDescription>
                  <CardTitle className="text-3xl">{catalog?.selection_strategy.total_models ?? '—'}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total catalog size</CardDescription>
                  <CardTitle className="text-3xl">{catalog?.selection_strategy.total_size_gb ? `${catalog.selection_strategy.total_size_gb} GB` : '—'}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Live feed updated</CardDescription>
                  <CardTitle className="text-2xl">{formatDate(catalog?.generated_at)}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Browser size cap</CardDescription>
                  <CardTitle className="text-3xl">{catalog?.selection_strategy.size_cap_gb ? `${catalog.selection_strategy.size_cap_gb} GB` : '—'}</CardTitle>
                </CardHeader>
              </Card>
            </section>

            <section className="space-y-4">
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold tracking-tight">Popular picks from the shelf</h2>
                <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
                  A quick look at some of the strongest draws in the catalog. These are sorted by hosted downloads, so you can see what people are actually reaching for.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {featuredModels.map((model) => (
                  <Card key={model.model_id} className="h-full">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <CardTitle className="text-xl">{model.name}</CardTitle>
                          <CardDescription className="pt-1">
                            {model.family || model.base_model?.id || model.author || 'Hosted model'}
                          </CardDescription>
                        </div>
                        {model.in_webllm_official && <Badge>Official WebLLM</Badge>}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm leading-6 text-muted-foreground">{model.description || 'Canada-hosted model ready for fast local and browser-driven workflows.'}</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">{model.quantization || 'Hosted'}</Badge>
                        {model.size_gb !== undefined && <Badge variant="outline">{model.size_gb.toFixed(3)} GB</Badge>}
                        {(model.use_cases || []).slice(0, 2).map((item) => (
                          <Badge key={item} variant="secondary">{item}</Badge>
                        ))}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm text-muted-foreground">
                        <div>
                          <div className="text-xs">Downloads</div>
                          <div className="font-medium text-foreground">{formatCompactNumber(model.downloads)}</div>
                        </div>
                        <div>
                          <div className="text-xs">License</div>
                          <div className="font-medium text-foreground">{model.base_model?.license || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs">Updated</div>
                          <div className="font-medium text-foreground">{formatDate(model.last_modified)}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section className="space-y-4">
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold tracking-tight">Browse the live catalog</h2>
                <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
                  Search it like a model shelf. Filter by use case. Narrow by size. Find something small enough for edge work or big enough for heavier lifting.
                </p>
              </div>

              <Card>
                <CardContent className="space-y-4 p-6">
                  <div className="grid gap-4 lg:grid-cols-[1.4fr_0.8fr]">
                    <div className="relative">
                      <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search by model, family, author, or use case"
                        className="pl-9"
                      />
                    </div>
                    <Select value={useCase} onValueChange={setUseCase}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by use case" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All use cases</SelectItem>
                        {useCaseOptions.map((item) => (
                          <SelectItem key={item} value={item}>
                            {item}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Tabs value={sizeBand} onValueChange={setSizeBand}>
                    <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
                      <TabsTrigger value="all">All sizes</TabsTrigger>
                      <TabsTrigger value="tiny">Tiny under 1 GB</TabsTrigger>
                      <TabsTrigger value="edge">Edge 1–2.5 GB</TabsTrigger>
                      <TabsTrigger value="workhorse">Workhorse 2.5–6 GB</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </CardContent>
              </Card>

              {loading ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Card key={index} className="h-72 animate-pulse bg-muted/40" />
                  ))}
                </div>
              ) : error ? (
                <Card>
                  <CardContent className="flex flex-col gap-4 p-6">
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <div className="flex flex-wrap gap-3">
                      <Button asChild variant="outline">
                        <a href={MODEL_METADATA_URL} target="_blank" rel="noreferrer">
                          Open metadata feed
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                      <Button asChild>
                        <a href="https://models2.mapleos.ai/" target="_blank" rel="noreferrer">
                          Open hosted index
                          <ArrowUpRight className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-muted-foreground">
                      Showing <span className="font-medium text-foreground">{visibleModels.length}</span> of{' '}
                      <span className="font-medium text-foreground">{filteredModels.length}</span> matching models.
                    </p>
                    <div className="text-sm text-muted-foreground">
                      Powered by the live metadata feed from <span className="font-medium text-foreground">models2.mapleos.ai</span>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {visibleModels.map((model) => (
                      <Card key={model.model_id} className="flex h-full flex-col">
                        <CardHeader className="space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <CardTitle className="text-xl leading-tight">{model.name}</CardTitle>
                              <CardDescription className="pt-1">
                                {model.author || 'Hosted by CanXP AI'} • {model.family || 'General family'}
                              </CardDescription>
                            </div>
                            {model.in_webllm_official ? <Badge>WebLLM official</Badge> : <Badge variant="secondary">Hosted</Badge>}
                          </div>
                          <p className="text-sm leading-6 text-muted-foreground">
                            {model.description || 'Canada-hosted model for browser, device, and lightweight deployment workflows.'}
                          </p>
                        </CardHeader>
                        <CardContent className="flex flex-1 flex-col justify-between gap-5">
                          <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                              {model.quantization && <Badge variant="outline">{model.quantization}</Badge>}
                              {model.size_gb !== undefined && <Badge variant="outline">{model.size_gb.toFixed(3)} GB</Badge>}
                              {(model.use_cases || []).slice(0, 3).map((item) => (
                                <Badge key={item} variant="secondary">{item}</Badge>
                              ))}
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="rounded-xl border p-3">
                                <div className="text-xs text-muted-foreground">Downloads</div>
                                <div className="font-semibold">{formatCompactNumber(model.downloads)}</div>
                              </div>
                              <div className="rounded-xl border p-3">
                                <div className="text-xs text-muted-foreground">Base license</div>
                                <div className="font-semibold">{model.base_model?.license || '—'}</div>
                              </div>
                              <div className="rounded-xl border p-3">
                                <div className="text-xs text-muted-foreground">Languages</div>
                                <div className="font-semibold">{model.base_model?.languages?.length ? model.base_model.languages.slice(0, 2).join(', ') : '—'}</div>
                              </div>
                              <div className="rounded-xl border p-3">
                                <div className="text-xs text-muted-foreground">Updated</div>
                                <div className="font-semibold">{formatDate(model.last_modified)}</div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {model.served_url && (
                              <Button asChild className="bg-canada-red text-white hover:bg-canada-red/90">
                                <a href={model.served_url} target="_blank" rel="noreferrer">
                                  Hosted files
                                  <Download className="ml-2 h-4 w-4" />
                                </a>
                              </Button>
                            )}
                            {model.huggingface_url && (
                              <Button asChild variant="outline">
                                <a href={model.huggingface_url} target="_blank" rel="noreferrer">
                                  Hugging Face
                                  <ExternalLink className="ml-2 h-4 w-4" />
                                </a>
                              </Button>
                            )}
                            {model.base_model?.url && (
                              <Button asChild variant="ghost">
                                <a href={model.base_model.url} target="_blank" rel="noreferrer">
                                  Base model
                                  <Globe className="ml-2 h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {visibleCount < filteredModels.length && (
                    <div className="flex justify-center pt-2">
                      <Button variant="outline" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                        Load more models
                      </Button>
                    </div>
                  )}
                </>
              )}
            </section>

            {catalog && (
              <section className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2 text-canada-red">
                      <Layers3 className="h-5 w-5" />
                      <CardTitle>What makes this catalog useful</CardTitle>
                    </div>
                    <CardDescription>
                      The point is not to pretend every strong model started in Canada. The point is to make strong models available through Canadian infrastructure.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm leading-6 text-muted-foreground">
                    <p>
                      This page is the front door to that idea. It gives users one place to browse what is live, what is lightweight enough for on-device work,
                      and what is officially supported for browser-first runtimes.
                    </p>
                    <p>
                      It also gives CanXP AI a cleaner story: we build sovereign systems like MaplePT, we specialize in SLM and on-device deployment,
                      and we make a wider shelf of useful models available through Canadian hosting when the job calls for it.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2 text-canada-red">
                      <Sparkles className="h-5 w-5" />
                      <CardTitle>Quantization guide</CardTitle>
                    </div>
                    <CardDescription>Quick shorthand for the formats you'll see across the shelf.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.entries(catalog.quantization_legend).slice(0, 4).map(([key, value]) => (
                      <div key={key} className="rounded-xl border p-3">
                        <div className="font-semibold">{key}</div>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">{value}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </section>
            )}
          </div>
        </main>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
};

export default Models;